import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styleColors from '../colors.styles';
import step2Image from '../assets/templates/editable-video-help/step-2.png';
import step3Image from '../assets/templates/editable-video-help/step-3.png';

const styles = {
  panel: {
    backgroundColor: styleColors.base.light,
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(4, 31, 65, 0.12)',
    height: '100%',
    maxHeight: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    p: '24px',
  },
  titleRow: {
    alignItems: 'center',
    color: '#041f41',
    display: 'flex',
    fontSize: '16px',
    fontWeight: 700,
    gap: 1,
    pb: 2,
  },
  titleIcon: {
    color: styleColors.base.walmart_blue,
    fontSize: 20,
  },
  titleDivider: {
    borderBottom: '1px solid #e9ecee',
    mb: 2,
  },
  stepCard: {
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    overflow: 'hidden',
    p: '16px',
  },
  stepRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 1.5,
  },
  stepContent: {
    flex: 1,
    minWidth: 0,
  },
  stepBadge: {
    alignItems: 'center',
    backgroundColor: styleColors.base.walmart_blue,
    borderRadius: '50%',
    color: styleColors.base.light,
    display: 'flex',
    flex: '0 0 24px',
    fontSize: '12px',
    fontWeight: 700,
    height: 24,
    justifyContent: 'center',
  },
  stepText: {
    color: '#041f41',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  screenshotWrap: {
    mt: 1.5,
    pl: '36px',
  },
  screenshot: {
    borderRadius: '8px',
    display: 'block',
    height: 'auto',
    maxWidth: '100%',
    width: '100%',
  },
};

const HELP_STEPS = [
  {
    id: 'add-media',
    text: (
      <Typography sx={styles.stepText}>
        Add an image or video for your preferred time frame. This will be
        replaced by your associate.
      </Typography>
    ),
  },
  {
    id: 'placeholder-tab',
    text: (
      <Typography sx={styles.stepText}>
        <Box component="span" fontWeight={700}>
          With your image/video selected,
        </Box>{' '}
        select the &ldquo;
        <Box component="span" fontWeight={700}>
          Placeholder
        </Box>
        &rdquo; tab on the right-hand side of the editor.
      </Typography>
    ),
    image: {
      src: step2Image,
      alt: 'Placeholder tab selected in the editor sidebar',
    },
  },
  {
    id: 'fill-settings',
    text: (
      <Typography sx={styles.stepText}>
        Scroll down to &ldquo;
        <Box component="span" fontWeight={700}>
          Fill
        </Box>
        &rdquo; and select &ldquo;
        <Box component="span" fontWeight={700}>
          Allow to Replace Fill Content
        </Box>
        &rdquo; and &ldquo;
        <Box component="span" fontWeight={700}>
          Act as Placeholder
        </Box>
        &rdquo; to ensure easy editing of your template.
      </Typography>
    ),
    image: {
      src: step3Image,
      alt: 'Fill settings with placeholder options enabled',
    },
  },
  {
    id: 'repeat-sections',
    text: (
      <Typography sx={styles.stepText}>
        Repeat for any section of your video that you would like your associate
        to replace!
      </Typography>
    ),
  },
];

export default function EditableVideoHelpPanel() {
  return (
    <Box sx={styles.panel} onMouseEnter={(event) => event.stopPropagation()}>
      <Typography component="h2" sx={styles.titleRow}>
        <InfoOutlinedIcon sx={styles.titleIcon} />
        How to create your video template
      </Typography>
      <Box sx={styles.titleDivider} />

      <Stack spacing={1.5}>
        {HELP_STEPS.map((step, index) => (
          <Box key={step.id} sx={styles.stepCard}>
            <Box sx={styles.stepRow}>
              <Box sx={styles.stepBadge}>{index + 1}</Box>
              <Box sx={styles.stepContent}>{step.text}</Box>
            </Box>
            {step.image && (
              <Box sx={styles.screenshotWrap}>
                <Box
                  component="img"
                  src={step.image.src}
                  alt={step.image.alt}
                  sx={styles.screenshot}
                />
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
